import _ from 'lodash';
import moment from 'moment';

const DATE_FORMAT = 'YYYY-MM-DD';

export const RESERVATION_GUEST_BOOKING_OPTION = { value: 'guest', label: 'Guest booking' };
export const RESERVATION_OWNER_BOOKING_OPTION = { value: 'owner', label: 'Owner booking' };
export const RESERVATION_HOLD_OPTION = { value: 'hold', label: 'Hold' };

export const GUEST_TYPES = {
  GUEST: 'guest',
  OWNER: 'owner',
  HOLD: 'hold',
};

export const RESERVATION_STATUS = {
  BOOKING: 'booking',
  GUEST: 'guest',
  HOLD: 'hold',
  LOCKOFF: 'lockoff',
  NONEXCLUSIVE: 'nonexclusive',
  OWNER: 'hold',
  UNKNOW: 'unknown',
};

export const RESERVATION_GUEST_TYPES = {
  OWNER: 'owner',
  GUEST: 'guest',
};

export const RESERVATION_STATUS_TYPES = {
  BOOKING: 'booking',
  HOLD: 'hold',
  NONEXCLUSIVE: 'nonexclusive',
  LOCKOFF: 'lockoff',
};

export const RESERVATION_TYPES = [
  { code: 'checkin', name: 'Check-in' },
  { code: 'checkout', name: 'Checkout' },
  { code: 'backToBack', name: 'Back to Back' },
];

export const RESERVATION_TYPES_BY_TYPE = {
  B2B: 'b2b',
  CHECKIN: 'checkin',
  CHECKOUT: 'checkout',
};
/**
 * @type {Object}
 */
export const RESERVATION_CATEGORIES = {
  BACK_TO_BACK: 'back-to-back',
  CHECKIN: 'checkin',
  CHECKOUT: 'checkout',
  OCCUPIED: 'occupied',
};

export const OCCUPANCY_TYPES = {
  checkin: 'Checkin',
  checkout: 'Check-out',
  occupied: 'Occupied',
};

/**
 * @type {Number}
 */
export const RECENT_RESERVATION_HOURS = 72;

/**
 * @type {Object}
 */
export const ADD_ONS_MAP = [
  {
    name: 'Early arrival',
    code: 'checkin_early',
  },
  {
    name: 'Late departure',
    code: 'checkout_late',
  },
];

/**
 * @type {Object}
 */
export const ADD_ONS_CODES = [
  {
    name: 'Early check-in',
    code: 'checkin_early',
  },
  {
    name: 'Late checkout',
    code: 'checkout_late',
  },
];

/**
 * @param {Object} reservation
 * @return {Array}
 */
export const getAddOnsFromReservation = reservation =>
  _.map(_.filter(ADD_ONS_MAP, addOn => _.get(reservation, addOn.code)), tag => _.get(tag, 'name'));

/**
 * @param {Moment} date
 * @param {Object} reservation
 * @return {String}
 */
export const getReservationCategory = (date, reservation) => {
  const checkinDate = _.get(reservation, 'checkin_date');
  const checkoutDate = _.get(reservation, 'checkout_date');

  if (date.isSame(checkinDate, 'day')) {
    return RESERVATION_CATEGORIES.CHECKIN;
  } else if (date.isSame(checkoutDate, 'day')) {
    return RESERVATION_CATEGORIES.CHECKOUT;
  } else if (date.isBetween(checkinDate, checkoutDate, 'days', '()')) {
    return RESERVATION_CATEGORIES.OCCUPIED;
  }

  return null;
};

/**
 * @param {Moment} reservationDate
 * @param {Object} reservations
 */
export const categorizeReservations = (reservationDate, reservations) => {
  const categorizedReservations = {};
  let numberOfOccupied = 0;

  _.forEach(reservations, reservation => {
    const reservationCategory = getReservationCategory(reservationDate, reservation);
    if (_.isEqual(reservationCategory, RESERVATION_CATEGORIES.OCCUPIED)) {
      numberOfOccupied += 1;
    }

    if (reservationCategory) {
      categorizedReservations[reservationCategory] = reservation;
    }
  });

  return { ...categorizedReservations, numberOfOccupied };
};

/* Group Reservations process */

/**
 * @return {Object}
 */
const createReservationGroupObject = () => ({
  all: [],
  allDates: [],
  allIds: [],
  guest: [],
  guestIds: [],
  owner: [],
  ownerIds: [],
  hold: [],
  holdIds: [],
});

/**
 * @return {Object}
 */
const createReservationGroupItem = (reservationDate, typeGuest, reservation) => {
  const reservationId = _.get(reservation, 'id');
  const checkinDate = _.get(reservation, 'checkin_date');
  const checkoutDate = _.get(reservation, 'checkout_date');

  return {
    checkin_date: checkinDate,
    checkout_date: checkoutDate,
    date: reservationDate,
    id: reservationId,
    status: typeGuest,
    typeGuest,
  };
};

const pushToReservationGroup = (
  group,
  reservationDate,
  typeGuest,
  reservation,
  reservationIds,
  typeReservation,
) => {
  if (typeGuest !== RESERVATION_STATUS_TYPES.HOLD) {
    group.all.push(createReservationGroupItem(reservationDate, typeGuest, reservation));
    group.allDates.push(reservationDate);
    group.allIds.push(...reservationIds);
  }

  if (typeReservation === RESERVATION_STATUS_TYPES.HOLD) {
    group.hold.push(createReservationGroupItem(reservationDate, typeGuest, reservation));
    group.holdIds.push(...reservationIds);
  } else if (typeGuest === RESERVATION_GUEST_TYPES.OWNER) {
    group.owner.push(createReservationGroupItem(reservationDate, typeGuest, reservation));
    group.ownerIds.push(...reservationIds);
  } else if (typeGuest === RESERVATION_GUEST_TYPES.GUEST) {
    group.guest.push(createReservationGroupItem(reservationDate, typeGuest, reservation));
    group.guestIds.push(...reservationIds);
  }
};

const isReservationInTimeframe = (date, startDate, endDate) =>
  moment(date).isBetween(moment(startDate), moment(endDate), 'd', '[]');

const isReservationBetweenTimeframe = (checkinDate, checkoutDate, startDate, endDate) => {
  const isStartDateBetween = isReservationInTimeframe(startDate, checkinDate, checkoutDate);
  const isEndDateBetween = isReservationInTimeframe(endDate, checkinDate, checkoutDate);

  return isStartDateBetween || isEndDateBetween;
};

/**
 * @param {String} checkinDate
 * @param {String} checkoutDate
 * @param {String} startDate
 * @param {String} endDate
 * @return {Object}
 */
const validateReservation = (checkinDate, checkoutDate, startDate, endDate) => {
  const validCheckin = isReservationInTimeframe(checkinDate, startDate, endDate);
  const validCheckout = isReservationInTimeframe(checkoutDate, startDate, endDate);
  const validBetween = isReservationBetweenTimeframe(checkinDate, checkoutDate, startDate, endDate);

  return {
    validBetween,
    validCheckin,
    validCheckout,
  };
};

/**
 * @param {Array.<Object>} reservations
 * @param {Array.<Number>} backToBackIds
 * @return {Array.<Object>}
 */
const filterBackToBack = (reservations, backToBackIds) =>
  _.filter(reservations, reservation => !_.includes(backToBackIds, reservation.id));

/**
 * @param {Object} reservationGroup
 * @param {Array.<Number>} backToBackIds
 * @return {Object}
 */
const filterReservationGroup = (reservationGroup, backToBackIds) => ({
  ...reservationGroup,
  all: filterBackToBack(reservationGroup.all, backToBackIds),
  allIds: _.difference(reservationGroup.allIds, backToBackIds),
  guest: filterBackToBack(reservationGroup.guest, backToBackIds),
  guestIds: _.difference(reservationGroup.guestIds, backToBackIds),
  owner: filterBackToBack(reservationGroup.owner, backToBackIds),
  ownerIds: _.difference(reservationGroup.ownerIds, backToBackIds),
});

/**
 * @param {Object} reservationGroup
 * @return {Object}
 */
const mapAllDates = reservationGroup => ({
  ...reservationGroup,
  allDates: _.map(reservationGroup.all, 'date'),
});

/**
 * @param {Array.<Object>} reservations
 * @param {Object} dates
 * @return {Object}
 */
export const groupReservations = (reservations, dates) => {
  const { startDate, endDate } = dates;

  // Recent reservations are with 3 days offset (in past)
  const recentReservationsMoment = moment().subtract(RECENT_RESERVATION_HOURS, 'hours');

  // Initialize reservation groups for checkins and checkouts
  const checkins = createReservationGroupObject();
  const checkouts = createReservationGroupObject();
  const backToBack = createReservationGroupObject();

  // Recent reservations based on recentReservationsMoment
  const recent = [];

  // Valid reservation flag
  let hasReservation = false;

  _.forEach(reservations, reservation => {
    if (reservation) {
      const reservationId = _.get(reservation, 'id', null);
      const checkinDate = _.get(reservation, 'checkin_date');
      const checkoutDate = _.get(reservation, 'checkout_date');
      const createdAt = _.get(reservation, 'created_at');
      const typeGuest = _.get(reservation, 'type_guest.code', '');
      const typeReservation = _.get(reservation, 'type_reservation.code', '');

      const { validBetween, validCheckin, validCheckout } = validateReservation(
        checkinDate,
        checkoutDate,
        startDate,
        endDate,
      );

      if (validCheckin || validCheckout || validBetween) {
        const reservationCreatedMoment = moment(createdAt);

        // Set valid reservation flag to true if reservation is in given date range
        hasReservation = true;

        if (reservationCreatedMoment.isAfter(recentReservationsMoment)) {
          recent.push(reservationId);
        }
      }

      // If valid reservation date push to checkin
      if (validCheckin) {
        pushToReservationGroup(
          checkins,
          checkinDate,
          typeGuest,
          reservation,
          [reservation.id],
          typeReservation,
        );
      }

      // If valid reservation date push to checkout
      if (validCheckout) {
        pushToReservationGroup(
          checkouts,
          checkoutDate,
          typeGuest,
          reservation,
          [reservation.id],
          typeReservation,
        );
      }
    }
  });

  // Back to back reservations
  const backToBackCheckinIds = [];
  const backToBackCheckoutIds = [];
  const backToBackReservations = _.groupBy([...checkins.all, ...checkouts.all], 'date');
  _.forEach(backToBackReservations, (b2bReservations, date) => {
    if (_.size(b2bReservations) > 1) {
      const b2bCheckin = _.find(b2bReservations, { checkin_date: date });
      const b2bCheckout = _.find(b2bReservations, { checkout_date: date });

      if (!_.isEmpty(b2bCheckin) && !_.isEmpty(b2bCheckout)) {
        const b2bTypeGuest = b2bCheckin.typeGuest;
        pushToReservationGroup(backToBack, date, b2bTypeGuest, b2bCheckin, [
          b2bCheckin.id,
          b2bCheckout.id,
        ]);

        backToBackCheckinIds.push(b2bCheckin.id);
        backToBackCheckoutIds.push(b2bCheckout.id);
      }
    }
  });

  const filteredCheckins = filterReservationGroup(checkins, backToBackCheckinIds);
  const filteredCheckouts = filterReservationGroup(checkouts, backToBackCheckoutIds);

  return {
    checkins: mapAllDates(filteredCheckins),
    checkouts: mapAllDates(filteredCheckouts),
    backToBack,
    hasReservation,
    recent,
  };
};

export const FILTER_RESERVATION_TYPES = [
  { code: 'checkinOnly', name: 'Check-in' },
  { code: 'checkoutOnly', name: 'Checkout' },
  { code: 'backToBack', name: 'Back to Back' },
];

export const FILTER_RESERVATION_GUEST_TYPES = [
  { code: 'checkinGuest', name: 'Guest Check-in' },
  { code: 'checkoutGuest', name: 'Guest Checkout' },
  { code: 'backToBackGuest', name: 'Guest Back to Back' },
];

export const FILTER_RESERVATION_OWNER_TYPES = [
  { code: 'checkinOwner', name: 'Owner Check-in' },
  { code: 'checkoutOwner', name: 'Owner Checkout' },
  { code: 'backToBackOwner', name: 'Owner Back to Back' },
];

/**
 * @param {Boolean} shouldDisplayOwnerCheckins
 * @param {Boolean} displayHoldReservations
 * @param {Boolean} displayOccupiedReservations
 * @return {Array}
 */
export const getReservationFilterOptions = (
  shouldDisplayOwnerCheckins,
  displayHoldReservations,
  displayOccupiedReservations,
) => {
  let options = [{ code: 'hasReservation', name: 'Any Reservation' }, ...FILTER_RESERVATION_TYPES];

  if (shouldDisplayOwnerCheckins) {
    options = [...options, ...FILTER_RESERVATION_OWNER_TYPES, ...FILTER_RESERVATION_GUEST_TYPES];
  }

  if (displayHoldReservations) {
    options = [...options, { code: 'hold', name: 'Hold' }];
  }

  if (displayOccupiedReservations) {
    options = [...options, { code: 'occupied', name: 'Occupied' }];
  }

  options = [
    ...options,
    { code: 'recent', name: 'Recently Added' },
    { code: 'noReservation', name: 'No reservation' },
  ];

  return options;
};

/**
 * @type {Object}
 */
export const RESERVATION_FILTER_MAP = {
  hasReservation: {
    reservation_event: 'any',
  },
  checkinOnly: {
    reservation_event: 'checkin',
  },
  checkoutOnly: {
    reservation_event: 'checkout',
  },
  backToBack: {
    reservation_event: 'back_to_back',
  },
  recent: {
    reservation_event: 'recent',
  },
  occupied: {
    reservation_event: 'occupied',
  },
  checkinOwner: {
    reservation_event: 'checkin',
    type_guest: 'owner',
  },
  checkoutOwner: {
    reservation_event: 'checkout',
    type_guest: 'owner',
  },
  backToBackOwner: {
    reservation_event: 'back_to_back',
    type_guest: 'owner',
  },
  checkinGuest: {
    reservation_event: 'checkin',
    type_guest: 'guest',
  },
  checkoutGuest: {
    reservation_event: 'checkout',
    type_guest: 'guest',
  },
  backToBackGuest: {
    reservation_event: 'back_to_back',
    type_guest: 'guest',
    type_reservation: 'booking',
  },
  hold: {
    type_reservation: 'hold',
  },
  noReservation: {
    reservation_event: 'no_reservation',
  },
};

/**
 * @param {Number} duration
 * @return {String}
 */
export const getDurationText = duration => `${duration} night${_.isEqual(duration, 1) ? '' : 's'}`;

export const mapFilters = ({ dates, searchFilter = '', listFilters }) => {
  const addOnFilters = _.get(listFilters, 'add_on');
  const guestFilters = _.get(listFilters, 'type_guest');

  const addOns = {
    early_checkin: _.includes(addOnFilters, 'checkin_early'),
    late_checkout: _.includes(addOnFilters, 'checkout_late'),
  };
  const addOnsFilter = _.mapValues(_.pickBy(addOns), () => 'yes');
  const dateFilter = dates
    ? {
        end_date: dates.endDate.format(DATE_FORMAT),
        start_date: dates.startDate.format(DATE_FORMAT),
      }
    : {};

  const preparedFilters = {
    ..._.omit(listFilters, 'add_on'),
    ...addOnsFilter,
    ...dateFilter,
    search: searchFilter,
    type_guest: _.without(guestFilters, GUEST_TYPES.HOLD),
  };

  if (!_.isEmpty(guestFilters)) {
    const typeReservationFilters = [];
    if (_.includes(guestFilters, GUEST_TYPES.HOLD)) {
      typeReservationFilters.push(GUEST_TYPES.HOLD);
    }
    if (
      _.includes(guestFilters, GUEST_TYPES.GUEST) ||
      _.includes(guestFilters, GUEST_TYPES.OWNER)
    ) {
      typeReservationFilters.push('booking');
    }
    if (!_.isEmpty(typeReservationFilters))
      preparedFilters.type_reservation = typeReservationFilters;
  }

  return preparedFilters;
};

/**
 * @param {Object} companySettings
 * @return {Array}
 */
export const getGuestOptions = (companySettings = {}) => {
  const options = [RESERVATION_GUEST_BOOKING_OPTION];
  if (companySettings.display_owner_checkins) {
    options.push(RESERVATION_OWNER_BOOKING_OPTION);
  }

  if (companySettings.display_hold_reservations) {
    options.push(RESERVATION_HOLD_OPTION);
  }

  return options;
};

export const hasSetFilters = filters => {
  let result = false;
  _.forEach(_.values(filters), value => {
    if (!_.isEmpty(value)) {
      result = true;
      return false;
    }
    return true;
  });
  return result;
};

export const getReservationGuestText = (reservation, schedule = false) => {
  const guestTypeName = _.get(reservation, 'type_guest.name', '');
  const reservationTypeCode = _.get(reservation, 'type_reservation.code');
  const reservationTypeName = _.get(reservation, 'type_reservation.name');
  const display = _.get(reservation, 'display', '');
  const displayGuestType = guestTypeName ? `(${guestTypeName})` : '';
  const isReservationTypeHold =
    reservationTypeCode === RESERVATION_STATUS.HOLD ||
    reservationTypeCode === RESERVATION_STATUS.UNKNOW;

  if (_.isEmpty(reservation)) return null;

  if (schedule && isReservationTypeHold) {
    return `HOLD ${displayGuestType}`;
  }

  if (schedule && reservationTypeCode === RESERVATION_STATUS.BOOKING) {
    return `${display} ${displayGuestType}`;
  }

  return `${guestTypeName} ${reservationTypeName}`;
};
